import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Menu,
  List,
  Skeleton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme
} from '@mui/material';
import {
  CheckCircle,
  ArrowForward,
  DoneAll
} from '@mui/icons-material';
import useNotificationStore, { Notification } from '@/store/NotificationStore';
import NotificationItem from './NotificationItem';

interface NotificationPanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  anchorEl,
  open,
  onClose
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === 'dark';

  const {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification
  } = useNotificationStore();

  // Fetch notifications when panel opens
  useEffect(() => {
    if (open) {
      fetchNotifications(1, 5);
    }
  }, [open, fetchNotifications]);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      onClose();
      navigate(notification.action_url);
    }
  };

  const handleViewAll = () => {
    onClose();
    navigate('/notifications');
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const displayedNotifications = notifications.slice(0, 5);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: { xs: 320, sm: 380 },
          maxHeight: 480,
          backgroundColor: isDark
            ? 'rgba(26, 31, 58, 0.98)'
            : 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(10px)',
          border: isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
          borderRadius: 2,
          mt: 1,
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 8px 32px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Box
              sx={{
                backgroundColor: theme.palette.error.main,
                color: '#fff',
                fontSize: '0.7rem',
                fontWeight: 600,
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                minWidth: 20,
                textAlign: 'center'
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Box>
          )}
        </Box>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<DoneAll sx={{ fontSize: '1rem !important' }} />}
            onClick={handleMarkAllAsRead}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              color: theme.palette.primary.main
            }}
          >
            Mark all read
          </Button>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
        {loading ? (
          <List sx={{ p: 0 }}>
            {[1, 2, 3].map(i => (
              <ListItem key={i} sx={{ py: 1.5, px: 2 }}>
                <ListItemIcon sx={{ minWidth: 44 }}>
                  <Skeleton variant="circular" width={36} height={36} />
                </ListItemIcon>
                <ListItemText
                  primary={<Skeleton variant="text" width="80%" />}
                  secondary={<Skeleton variant="text" width="50%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : displayedNotifications.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              px: 2
            }}
          >
            <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              All caught up!
            </Typography>
            <Typography variant="body2" color="text.disabled">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {displayedNotifications.map((notification, index) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onDismiss={dismissNotification}
                onClick={handleNotificationClick}
                compact
                showDivider={index < displayedNotifications.length - 1}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Divider />
      <Box
        sx={{
          p: 1,
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <Button
          fullWidth
          endIcon={<ArrowForward />}
          onClick={handleViewAll}
          sx={{
            textTransform: 'none',
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          View All Notifications
        </Button>
      </Box>
    </Menu>
  );
};

export default NotificationPanel;
